import AddIcon from '@mui/icons-material/AddRounded';
import { Button } from '@ovComponents/2-component/button/button';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents as analyticsEvents } from '@utils/analytics/analytics';
import { useAnalyticsContext } from '@utils/analytics/contextDetection';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal } from '../formModal/formModal';

export type CreateNewModalProp = {
  onSubmit?: (event: any) => void | Promise<void>;
  loading?: boolean;
  children?: any[] | any;
  title?: string;
  fullWidth?: boolean;
  sx?: any;
  buttonType?: 'BUTTON' | 'ICON' | 'NONE';
  maxWidth?: string;
  disabled?: boolean;
  modalOpenDisabled?: boolean;
  iconType?: 'ADD' | 'EDIT' | 'DOT';
  buttonText?: string;
  hideSubmit?: boolean;
  modalButton?: string;
  icon?: any;
  buttonVariant?: 'outlined' | 'text' | 'contained' | 'tonal' | undefined;
  forceOpen?: boolean;
  onClose?: () => void;
};

export const CreateNewModal = ({
  onSubmit,
  loading,
  children,
  title,
  fullWidth = false,
  sx,
  buttonType = 'BUTTON',
  maxWidth = 'sm',
  buttonText,
  disabled = false,
  disableButton = false,
  icon = AddIcon,
  modalButton,
  buttonVariant = 'filled',
  forceOpen = false,
  onClose,
  state,
  initialState,
  testId,
  modalType = 'generic-create',
  context,
}: {
  onSubmit: (event: any) => void;
  loading: boolean;
  children: any[] | any;
  title: string;
  fullWidth?: boolean;
  sx?: any;
  buttonType?: 'BUTTON' | 'ICON' | 'NONE';
  maxWidth?: string;
  disabled?: boolean;
  disableButton?: boolean;
  iconType?: 'ADD' | 'EDIT' | 'DOT';
  buttonText?: string;
  modalButton?: string;
  icon?: any;
  buttonVariant?: 'outlined' | 'text' | 'filled' | 'tonal' | undefined;
  forceOpen?: boolean;
  onClose?: () => void;
  state?: any;
  initialState?: any;
  testId?: string;
  modalType?: string;
  context?: string;
}) => {
  const { t } = useTranslation(['shared']);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { getModalType } = useAnalyticsContext();
  const [open, setOpen] = useState(forceOpen);

  // Automatically detect modal type based on current page
  const detectedModalType = getModalType(modalType);

  const handleClickOpen = () => {
    setOpen(true);

    // Track add init event for fee grids and billing schedules (skip generic modal tracking)
    if (detectedModalType === 'fee-grid') {
      sendAnalytic(analyticsEvents.feeGridAddInit, {
        modalTitle: title,
        previousPage: document.referrer || 'direct',
        timestamp: new Date().toISOString(),
      });
    } else if (detectedModalType === 'billing-schedule') {
      sendAnalytic(analyticsEvents.billingScheduleAddInit, {
        modalTitle: title,
        previousPage: document.referrer || 'direct',
        timestamp: new Date().toISOString(),
      });
    } else {
      // Track modal open event for other types
      sendAnalytic(analyticsEvents.modalOpen(detectedModalType, context), {
        modalTitle: title,
        buttonType,
        previousPage: document.referrer || 'direct',
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    setOpen(false);
    // Track modal close event
    sendAnalytic(analyticsEvents.modalClose(detectedModalType, context), {
      modalTitle: title,
      previousPage: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
    });
  };

  const submit = async (event: any) => {
    try {
      await onSubmit(event);
      setOpen(false);
      // Note: FormModal will handle the create event tracking
    } catch (error) {
      console.error('validation errors', error);
      // Track modal close event (covers both user cancellation and error closing)
      sendAnalytic(analyticsEvents.modalClose(detectedModalType, context), {
        modalTitle: title,
        error: error instanceof Error ? error.message : 'Unknown error',
        previousPage: document.referrer || 'direct',
        timestamp: new Date().toISOString(),
      });
    }
  };

  return (
    <>
      {buttonType === 'NONE' ? (
        <Button
          label={modalButton || t('shared:add')}
          variant='text'
          disabled={disableButton}
          onClick={handleClickOpen}
          sx={sx}
          fullWidth={fullWidth}
          dataTestId={testId ? `${testId}-add-button` : 'new-modal-add-button'}
        />
      ) : buttonType === 'ICON' ? (
        <IconButton
          disabled={disableButton}
          style={{ marginTop: '-2px' }}
          data-testid='add-button'
          onClick={handleClickOpen}
          testId={testId ? `${testId}-add-button` : 'new-modal-add-button'}
        >
          {icon}
        </IconButton>
      ) : (
        <Button
          variant={buttonVariant}
          disabled={disableButton}
          onClick={handleClickOpen}
          sx={sx}
          fullWidth={fullWidth}
          leadingIcon={icon}
          label={modalButton || t('shared:add')}
          dataTestId={testId ? `${testId}-add-button` : 'new-modal-add-button'}
        />
      )}
      <FormModal
        onSubmit={submit}
        loading={loading}
        disabled={disabled}
        children={children}
        title={title}
        open={open}
        handleClose={handleClose}
        formButton={buttonText || t('shared:create')}
        maxWidth={maxWidth}
        state={state}
        initialState={initialState}
        testId={testId}
      />
    </>
  );
};

export default CreateNewModal;
